import React, { useState } from "react";
import * as resellerService from "@/shared/api/services/resellerService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Power,
  PowerOff,
  Building2,
  CheckCircle2,
  XCircle,
  Clock,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import ResellerMerchantForm from "@/shared/components/reseller/ResellerMerchantForm";
import { LanguageProvider } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  ACTIVE: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "Active" },
  SUSPENDED: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  suspended: { icon: XCircle, color: "bg-red-100 text-red-700", label: "Suspended" },
  INACTIVE: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Inactive" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "Pending" },
};

function ResellerMerchantsContent() {
  const [search, setSearch] = useState("");
  const [suspendDialog, setSuspendDialog] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const { t, language } = useLanguage();

  const resellerName = "Asia Gaming Network";
  const resellerCode = "RSL001";

  const { data: merchants = [], isLoading } = useQuery({
    queryKey: ["reseller-merchants"],
    queryFn: () => resellerService.getMyMerchants(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => resellerService.updateMerchant(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["reseller-merchants"]);
      setSuspendDialog(null);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data) => resellerService.createMerchant(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["reseller-merchants"]);
      setShowForm(false);
      setEditingMerchant(null);
      setSaving(false);
    },
  });

  const handleSaveMerchant = async (data) => {
    setSaving(true);
    try {
      if (editingMerchant) {
        // Update merchant
        await updateMutation.mutateAsync({ id: editingMerchant.id, data });
      } else {
        // Create merchant - only send backend-required fields
        const createData = {
          email: data.email,
          password: data.password,
          name: data.name,
          webhook: data.webhook_url || undefined,
          callback: data.callback_url || undefined,
        };
        await createMutation.mutateAsync(createData);
      }
    } catch (error) {
      // Error is handled by global error handler
    } finally {
      setSaving(false);
      setShowForm(false);
      setEditingMerchant(null);
    }
  };

  const handleEditMerchant = (merchant) => {
    setEditingMerchant(merchant);
    setShowForm(true);
  };

  const filteredMerchants = merchants.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.code?.toLowerCase().includes(search.toLowerCase())
  );

  const getTodayVolume = (code) => {
    // TODO: Implement transaction volume query from backend
    // For now, return placeholder value
    return 0;
  };

  const handleToggleStatus = async () => {
    if (!suspendDialog) return;
    const currentStatus = suspendDialog.status?.toUpperCase();
    const newStatus = currentStatus === "ACTIVE" ? "SUSPENDED" : "ACTIVE";
    await updateMutation.mutateAsync({ id: suspendDialog.id, data: { name: suspendDialog.name, status: newStatus } });
  };

  return (
    <ResellerLayout currentPage="ResellerMerchants" resellerName={resellerName} resellerCode={resellerCode}>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("merchants")}</h1>
            <p className="text-slate-500">{merchants.length} {t("merchants").toLowerCase()}</p>
          </div>
          <Button className="gap-2 bg-violet-600 hover:bg-violet-700" onClick={() => { setEditingMerchant(null); setShowForm(true); }}>
            <Plus className="h-4 w-4" />
            {t("addMerchant")}
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder={t("search")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("merchant")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">{t("code")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">{t("today")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden lg:table-cell">{t("created")}</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-500">{t("loading")}</td></tr>
                ) : filteredMerchants.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-slate-500">{t("noMerchantsFound")}</td></tr>
                ) : (
                  filteredMerchants.map((m, i) => {
                    const status = statusConfig[m.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    const volume = getTodayVolume(m.code);
                    return (
                      <motion.tr
                        key={m.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="hover:bg-slate-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                              <Building2 className="h-5 w-5 text-violet-600" />
                            </div>
                            <div>
                              <p className="font-medium">{m.name}</p>
                              <p className="text-sm text-slate-500 sm:hidden">{m.code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <Badge variant="outline">{m.code}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${status.color} gap-1 border-0`}>
                            <StatusIcon className="h-3 w-3" />
                            <span className="hidden sm:inline">{t(m.status?.toLowerCase())}</span>
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold hidden md:table-cell">
                          ฿{volume.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-slate-500 hidden lg:table-cell">
                          {m.created_date ? format(new Date(m.created_date), language === "th" ? "d MMM yyyy" : "MMM d, yyyy", { locale: language === "th" ? th : enUS }) : "N/A"}
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditMerchant(m)}><Pencil className="h-4 w-4 mr-2" />{t("edit")}</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setSuspendDialog(m)}>
                                {m.status === "active" ? (
                                  <><PowerOff className="h-4 w-4 mr-2" />{t("suspend")}</>
                                ) : (
                                  <><Power className="h-4 w-4 mr-2" />{t("activate")}</>
                                )}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <AlertDialog open={!!suspendDialog} onOpenChange={() => setSuspendDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {suspendDialog?.status === "active" ? t("suspendMerchantTitle") : t("activateMerchantTitle")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {suspendDialog?.status === "active"
                  ? `${t("suspendMerchantConfirm")} "${suspendDialog?.name}"? ${t("willNotProcessTransactions")}`
                  : `${t("activateMerchantConfirm")} "${suspendDialog?.name}"?`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleToggleStatus}
                className={suspendDialog?.status === "active" ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}
              >
                {suspendDialog?.status === "active" ? t("suspend") : t("activate")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ResellerMerchantForm
          open={showForm}
          onClose={() => { setShowForm(false); setEditingMerchant(null); }}
          merchant={editingMerchant}
          onSave={handleSaveMerchant}
          saving={saving}
        />
      </div>
    </ResellerLayout>
  );
}

export default function ResellerMerchants() {
  return (
    <LanguageProvider>
      <ResellerMerchantsContent />
    </LanguageProvider>
  );
}